import {createI18n} from 'vue-i18n';
import {useUserStore} from '~/store/user';
import en from '~/assets/lang/en';
import da from '~/assets/lang/da';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import duration from 'dayjs/plugin/duration';
import localeData from 'dayjs/plugin/localeData';
import isToday from 'dayjs/plugin/isToday';
import isYesterday from 'dayjs/plugin/isYesterday';
import isoWeek from 'dayjs/plugin/isoWeek';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import 'dayjs/locale/da';
import 'dayjs/locale/en';
import type {Pinia} from "pinia";

const messages = {
  en: en,
  da: da
};

export default defineNuxtPlugin(({vueApp, $pinia}) => {
  // Access the user store
  const userStore = useUserStore($pinia as Pinia);

  // Determine the browser language only if running on the client
  let browserLanguage = 'en';

  if (import.meta.client) {
    browserLanguage = navigator.language.split('-')[0]; // Simplified to primary language code
  }

  // Use the user's language or fall back to the browser's or default to 'en'
  const initialLocale = userStore.userLanguage || browserLanguage || 'en';

  // Set Moment.js locale based on initial locale
  dayjs.locale(initialLocale);
  dayjs.extend(duration);
  dayjs.extend(relativeTime);
  dayjs.extend(localeData);
  dayjs.extend(isoWeek);
  dayjs.extend(isToday);
  dayjs.extend(isYesterday);
  dayjs.extend(customParseFormat);
  dayjs.extend(isSameOrBefore);
  dayjs.extend(isSameOrAfter);

  // Create the i18n instance
  const i18n = createI18n({
    legacy: false,
    globalInjection: true,
    locale: initialLocale, // Set the locale based on user preference or browser language
    fallbackLocale: 'en', // Set fallback locale
    messages, // Provide the locale messages
    missingWarn: false,
    fallbackWarn: false
  });

  // Watch for changes to `userLanguage` in the store and update the i18n locale and Moment.js locale
  watch(
    () => userStore.userLanguage,
    (newLanguage) => {
      if (newLanguage) {
        i18n.global.locale.value = newLanguage as "da" | "en";
        dayjs.locale(newLanguage); // Update Moment.js locale
      }
    },
    {immediate: true}
  );

  // Add the i18n instance to the Vue app
  vueApp.use(i18n);

  return {
    provide: {
      dayjs
    }
  }
});